import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  loginUser,
  logout as apiLogout,
  getCurrentUser,
  getCurrentUserFromApi,
  refreshToken,
} from '../../services/apiServices';
import { getOrganizationById } from '../../services/real/organizationService.real';
import { getOnboardingStatus } from '../../services/onboardingService';
import { isOrganizationComplete } from '../../pages/CreateOrganization/utils/isOrganizationComplete';
import { getLoginErrorMessage } from '../../utils/errorHandler';
import { getRedirectPathAfterLogin } from '../../utils/getRedirectPathAfterLogin';

/**
 * This file contains asynchronous thunks for authentication-related operations,
 * leveraging Redux Toolkit's createAsyncThunk for handling API calls and state updates.
 *
 * Thunks Included:
 * - login: Handles user login, stores tokens, and retrieves user data.
 * - logout: Clears tokens, user data, and resets authentication state.
 * - refreshAuthToken: Refreshes the access token to maintain the session.
 * - initializeAuth: Initializes authentication state based on stored data.
 *
 * Purpose:
 * Provides centralized and reusable logic for managing authentication actions
 * while ensuring predictable state updates and error handling.
 */

/** Normalize API user payload: support { data: user } or user. */
const normalizeUserPayload = (payload) => payload?.data ?? payload ?? null;

/** Map API user (organizationId) to app shape so selectors get orgId from user.organization. */
const normalizeUserForApp = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    ...raw,
    organization: raw.organization ?? raw.organizationId,
  };
};

/**
 * Login thunk: real login → persist token → fetch current user from API → persist user.
 * Uses getCurrentUserFromApi so we always hit the API after login (no mock in this flow).
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password, rememberMe }, { rejectWithValue }) => {
    try {
      const loginResponse = await loginUser(username, password);
      const payload = loginResponse?.data ?? loginResponse ?? {};
      const { access_token, refresh_token } = payload;

      if (!access_token) {
        throw new Error('Login succeeded but no access_token received');
      }

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('accessToken', access_token);
      if (refresh_token) storage.setItem('refreshToken', refresh_token);

      const userData = await getCurrentUserFromApi();
      const rawUser = normalizeUserPayload(userData);
      const user = normalizeUserForApp(rawUser);
      storage.setItem('user', JSON.stringify(user));

      const orgId = user?.organization ?? user?.organizationId;
      if (orgId) storage.setItem('organizationId', String(orgId));

      let createOrgStep = 1;
      const userPrimaryOrgDataMissing =
        user?.fullName == null ||
        user?.fullName === '' ||
        user?.phoneNumber == null ||
        user?.phoneNumber === '';
      // Only fetch org and onboarding/status when user has an org (else → create-organization page, no status call).
      let onboardingData = null;
      if (orgId) {
        try {
          const orgResponse = await getOrganizationById(orgId);
          const org = orgResponse?.data ?? orgResponse ?? null;
          const orgComplete = isOrganizationComplete(org);
          // Step 2 when org is complete OR when user lacks primary-org data (fullName/phoneNumber)
          createOrgStep = orgComplete || userPrimaryOrgDataMissing ? 2 : 1;
          // Onboarding status only after getCurrentUser + org; and only when user has org (not create-org flow).
          try {
            const statusRaw = await getOnboardingStatus();
            onboardingData = statusRaw?.data ?? statusRaw ?? null;
          } catch {
            onboardingData = null;
          }
        } catch {
          createOrgStep = userPrimaryOrgDataMissing ? 2 : 1;
        }
      }

      const redirectTo = getRedirectPathAfterLogin({ user, createOrgStep, onboardingData });
      return { user, createOrgStep, onboardingData, redirectTo };
    } catch (error) {
      const friendlyErrorMessage = getLoginErrorMessage(error);
      return rejectWithValue(friendlyErrorMessage);
    }
  },
);

const AUTH_STORAGE_KEYS = ['accessToken', 'refreshToken', 'user', 'organizationId'];

const clearAuthFromStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// Logout Thunk – clear auth from both localStorage and sessionStorage (same as login/initializeAuth)
export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await apiLogout();
  } catch {
    // Continue clearing local state even if API call fails
  } finally {
    clearAuthFromStorage();
    sessionStorage.clear();
  }
  return undefined;
});

// Refresh Token Thunk
export const refreshAuthToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const { access_token } = await refreshToken();

      localStorage.setItem('accessToken', access_token);
      return access_token;
    } catch (error) {
      return rejectWithValue('Failed to refresh token');
    }
  },
);

// Initialize Auth Thunk – restore session from storage (must read from both localStorage and sessionStorage to match login)
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      const accessToken =
        localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const userRaw =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;

      if (!accessToken || !user) {
        throw new Error('No valid authentication data found.');
      }

      return {
        user,
        isAuthenticated: true,
      };
    } catch (error) {
      return rejectWithValue('Failed to initialize authentication');
    }
  },
);

// Function to authenticate using only the access_token.
// This function accepts an access_token as its sole parameter and verifies it to log the user in,
// bypassing the traditional username/password authentication process.
export const authenticateWithToken = createAsyncThunk(
  'auth/authenticateWithToken',
  async ({ accessToken, rememberMe }, { rejectWithValue }) => {
    try {
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('accessToken', accessToken);
      storage.setItem('refreshToken', refreshToken);
      const userData = await getCurrentUser();
      storage.setItem('user', JSON.stringify(userData));

      return userData;
    } catch (error) {
      return rejectWithValue('Failed to initialize authentication');
    }
  },
);

// Get-Current-User Thunk
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const userData = await getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch current user');
    }
  },
);
